import memoize from 'lru-memoize';
import React, {Component} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {FrontEvent} from '../../../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {countTrueValues} from '../../../../../../../libs/shared/core/src/helpers/utilities/arrayHelpers';
import {TeamModel} from '../../../../../../../libs/shared/core/src/models/api/teamModel';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../../../../libs/shared/core/src/services/tracking/tracking';
import {ResourceMap} from '../../../../../../../libs/shared/core/src/store/commonAppTypes';
import {FolderBreadcrumbs} from '../../../../../../../libs/shared/ui/src/components/breadcrumbs/folderBreadcrumbs';
import {ButtonVariantsEnum} from '../../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {IconButton} from '../../../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {DropdownProps} from '../../../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownCategory} from '../../../../../../../libs/shared/ui/src/components/dropdowns/dropdownCategory';
import {DropdownEmpty} from '../../../../../../../libs/shared/ui/src/components/dropdowns/dropdownEmpty';
import {DropdownItem} from '../../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {DropdownItemCheckbox} from '../../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemCheckbox';
import {DropdownItemChevron} from '../../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemChevron';
import {DropdownItemContent} from '../../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {DropdownItemIconImage} from '../../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemIconImage';
import {
  AsyncSearchDropdown,
  AsyncSearchDropdownDataFetcher,
} from '../../../../../../../libs/shared/ui/src/components/dropdowns/search/asyncSearchDropdown';
import {VisualSizesEnum} from '../../../../../../../libs/shared/ui/src/styles/commonStyles';
import {flexCenter} from '../../../../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import noTags from '../../../../../../assets/images/noTags.svg';
import {BulkActionTypesEnum} from '../../../../../../helpers/bulkActionHelpers';
import {ViewerDropdownInterfaceEventSourcesEnum} from '../../../../../../helpers/models/tagHelpers';
import {ConversationBaseModel} from '../../../../../../models/api/conversationModel';
import {LegacyConversationLinkDropdownEntryPointModel} from '../../../../../../models/api/legacyEntryPointModel';
import {SafetyNetRequiredTaggingModel} from '../../../../../../models/api/safetyNetModel';
import {SearchTagModel} from '../../../../../../models/api/searchTagModel';
import {TagModel} from '../../../../../../models/api/tagModel';
import {DropdownItemAccentIcon} from '../../../../../common/dropdowns/dropdownItemAccentIcon';
import {TagDropdownItem} from '../../../../../common/dropdowns/tagDropdownItem';
import {BulkActionConfirmationDialog} from '../../actions/bulk/bulkActionConfirmationDialog';
import {RequiredTaggingTooltip} from '../../safetyNets/dialogs/requiredTaggingTooltip';
import {AddRequiredTagItem} from '../requiredTagging/addRequiredTagItem';
import {TagDropdownCreateButton} from './tagDropdownCreateButton';

/*
 * Constants.
 */

const defaultWidth = 320;

/*
 * Types.
 */

export enum TagDropdownItemTypesEnum {
  RECENT_TAG,
  RECENT_TAG_HEADER,
  ENTRY_POINT,
  ENTRY_POINT_CUSTOM_LINK,
  ENTRY_POINT_HEADER,
  SEARCH_TAG,
  SEARCH_TAG_HEADER,
  SEARCH_TAG_BREADCRUMBS,
  EMPTY_LABEL,
  REQUIRED_TAGGING,
  REQUIRED_TAGGING_HEADER,
}

interface DropdownRecentTagItem {
  type: TagDropdownItemTypesEnum.RECENT_TAG;
  tag: SearchTagModel;
}

interface DropdownRecentTagHeaderItem {
  type: TagDropdownItemTypesEnum.RECENT_TAG_HEADER;
}

interface DropdownEntryPointItem {
  type: TagDropdownItemTypesEnum.ENTRY_POINT;
}

interface DropdownEntryPointCustomLinkItem {
  type: TagDropdownItemTypesEnum.ENTRY_POINT_CUSTOM_LINK;
}

interface DropdownEntryPointHeaderItem {
  type: TagDropdownItemTypesEnum.ENTRY_POINT_HEADER;
}

interface DropdownSearchTagItem {
  type: TagDropdownItemTypesEnum.SEARCH_TAG;
  tag: SearchTagModel;
}

interface DropdownSearchTagHeaderItem {
  type: TagDropdownItemTypesEnum.SEARCH_TAG_HEADER;
}

interface DropdownSearchTagBreadcrumbsItem {
  type: TagDropdownItemTypesEnum.SEARCH_TAG_BREADCRUMBS;
}

interface DropdownEmptySearchTagItem {
  type: TagDropdownItemTypesEnum.EMPTY_LABEL;
}

interface DropdownRequiredTaggingItem {
  type: TagDropdownItemTypesEnum.REQUIRED_TAGGING;
  requiredTaggingSafetyNet: SafetyNetRequiredTaggingModel;
}

interface DropdownRequiredTaggingHeaderItem {
  type: TagDropdownItemTypesEnum.REQUIRED_TAGGING_HEADER;
}

export type TagDropdownItem =
  | DropdownRecentTagItem
  | DropdownRecentTagHeaderItem
  | DropdownEntryPointItem
  | DropdownEntryPointCustomLinkItem
  | DropdownEntryPointHeaderItem
  | DropdownSearchTagItem
  | DropdownSearchTagHeaderItem
  | DropdownSearchTagBreadcrumbsItem
  | DropdownEmptySearchTagItem
  | DropdownRequiredTaggingItem
  | DropdownRequiredTaggingHeaderItem;

/*
 * Props.
 */

interface PaginationData {
  hasFetchedInitialItems: boolean;
  offset: number;
}
export type AddTopicOrTagDropdownDataFetcher = AsyncSearchDropdownDataFetcher<
  TagDropdownItem,
  PaginationData
>;

export interface AddTopicOrTagDropdownProps extends DropdownProps {
  teams: ResourceMap<TeamModel>;
  userTeams: ReadonlyArray<TeamModel>;
  conversations: ReadonlyArray<ConversationBaseModel>;
  entryPoints: ReadonlyArray<LegacyConversationLinkDropdownEntryPointModel>;
  fetchTagsAsync: AddTopicOrTagDropdownDataFetcher;
  onCreateTagSelect(e: FrontEvent | undefined, name: string): void;
  onAddTopicSelect: ((event: FrontEvent) => void) | undefined;
  onEntryPointSelect: ((model: LegacyConversationLinkDropdownEntryPointModel) => void) | undefined;
  tagConversation: (model: SearchTagModel) => void;
  untagConversation: (model: TagModel) => void;
  bulkTagConversation: (model: SearchTagModel) => void;
  bulkUntagConversation: (model: TagModel) => void;
  isBulkActionRequired: boolean;
  interfaceEventSource: ViewerDropdownInterfaceEventSourcesEnum;
  navigationTagsPath?: ReadonlyArray<TagModel>;
  setNavigationTagsPath?: (arg: ReadonlyArray<TagModel>) => void;
  searchQuery: string;
  setSearchQuery: (arg: string) => void;
}

interface AddTopicOrTagDropdownState {
  tagToBulkAdd?: SearchTagModel;
  tagToBulkRemove?: TagModel;
  openedAt: number;
}

/*
 * Style.
 */

const StyledEmptyWrapperDiv = styled.div`
  height: 275px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const StyledEmptyContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 151px;
`;

const StyledDropdownEmpty = styled(DropdownEmpty)`
  flex: 0;
  padding: 0;
`;

const StyledEmptyButtonDiv = styled.div`
  max-width: 100%;
  display: flex;
  flex-direction: row;
`;

const StyledIconButton = styled(IconButton)`
  padding: 3px;
`;

const StyledRequiredTaggingCategory = styled(DropdownCategory)`
  ${flexCenter}
`;

const StyledRequiredTaggingTooltip = styled(RequiredTaggingTooltip)`
  margin-left: 4px;
`;

const StyledRequiredTaggingStarDiv = styled.div`
  color: ${(p) => p.theme.palette.red.shade40};
  margin-left: 2px;
`;

/*
 * Component.
 */

export class AddTopicOrTagDropdown extends Component<AddTopicOrTagDropdownProps, AddTopicOrTagDropdownState> {
  constructor(props: AddTopicOrTagDropdownProps) {
    super(props);
    this.state = {
      openedAt: performance.now(),
    };
  }

  /*
   * Event handlers.
   */

  private readonly onToggleTag = (
    event: FrontEvent,
    searchTag: SearchTagModel,
    isChecked: boolean,
    interfaceEventSection: string,
  ) => {
    event.preventDefault();

    if (this.props.isBulkActionRequired) {
      this.onBulkToggleTag(searchTag, isChecked);
    } else {
      this.onNormalToggleTag(searchTag, isChecked);
    }

    trackInterfaceEvent(InterfaceEventsEnum.P3_ADD_TAG_USING_DROPDOWN, {
      action: isChecked ? 'remove' : 'add',
      num_conversations: this.props.conversations.length,
      tag_id: isChecked ? null : searchTag[0].id,
      time_til_tag: performance.now() - (this.state.openedAt ?? 0),
      section: interfaceEventSection,
      is_bulk: this.props.isBulkActionRequired,
      location: this.props.interfaceEventSource,
    });
  };

  private readonly onNormalToggleTag = (searchTag: SearchTagModel, isChecked: boolean) => {
    if (isChecked) {
      this.props.untagConversation(searchTag[0]);
    } else {
      this.props.tagConversation(searchTag);
    }
  };

  private readonly onBulkToggleTag = (searchTag: SearchTagModel, isChecked: boolean) => {
    if (isChecked) {
      this.setState({tagToBulkRemove: searchTag[0]});
    } else {
      this.setState({tagToBulkAdd: searchTag});
    }
  };

  private readonly getSectionName = (type: TagDropdownItemTypesEnum) => {
    switch (type) {
      case TagDropdownItemTypesEnum.RECENT_TAG:
        return 'recent';
      default:
        return 'all_tags';
    }
  };

  /*
   * Render.
   */

  render() {
    return (
      <>
        <AsyncSearchDropdown
          {...this.props}
          key={this.props.navigationTagsPath?.[0]?.id}
          width={this.props.width || defaultWidth}
          headerTitle={<Trans id="dropdown-tags-header">Tags</Trans>}
          query={this.props.searchQuery}
          onQueryChange={(newQuery) => this.props.setSearchQuery(newQuery)}
          onSearchInputEnter={(e, query) => this.props.onCreateTagSelect(e, query)}
          headerBackButtonLabel={<Trans id="add-topic-or-tag-dropdown-navigate-back">Back</Trans>}
          onHeaderBackButtonClick={this.makeOnHeaderBackButtonClick()}
          headerRightIcon={this.renderAddTagButton()}
          maxContentHeight={595}
          onRequestData={this.props.fetchTagsAsync}
          renderRow={this.renderRow}
          renderEmpty={this.renderEmpty}
          transformDataForRender={this.makeTransformDataForRender(this.props)}
          searchInputPlaceholder={
            this.props.navigationTagsPath && this.props.navigationTagsPath.length > 0
              ? `Search under "${this.props.navigationTagsPath[0].name}"`
              : 'Search'
          }
        />
        {this.maybeRenderBulkAddConfirmation()}
        {this.maybeRenderBulkRemoveConfirmation()}
      </>
    );
  }

  private makeOnHeaderBackButtonClick() {
    const {navigationTagsPath, setNavigationTagsPath} = this.props;
    if (navigationTagsPath !== undefined && navigationTagsPath.length > 0) {
      return () => {
        trackInterfaceEvent(InterfaceEventsEnum.P3_NAVIGATE_TAG_DROPDOWN, {
          action: 'navigate_via_back_button',
        });
        const newPath = navigationTagsPath.slice(1);
        this.props.setSearchQuery('');
        return setNavigationTagsPath?.(newPath);
      };
    }

    return undefined;
  }

  private makeTransformDataForRender(props: AddTopicOrTagDropdownProps) {
    return (data: ReadonlyArray<TagDropdownItem>, query: string) => {
      // Only display "top-level" tags.
      if (props.navigationTagsPath?.length === 0) {
        return data;
      }

      // Display a nested tag folder view, i.e., not "top-level."
      if (props.navigationTagsPath !== undefined && props.navigationTagsPath.length > 0) {
        // Exclude recently used/integrations/etc.
        const isSearching = Boolean(query);
        const dataInParentFolder = calculateDataInParentFolder(isSearching, props.navigationTagsPath, data);
        // Only show the breadcrumbs when we have data, so we otherwise show the empty search overlay.
        const maybeBreadCrumbs = dataInParentFolder.length
          ? [{type: TagDropdownItemTypesEnum.SEARCH_TAG_BREADCRUMBS as const}]
          : [];

        // The parent tag item will be when we're browsing it's children, so we add it in anyway so
        // the user has a way to select the parent tag.
        const maybeParentTagItem =
          dataInParentFolder.length &&
          !isSearching &&
          !dataInParentFolder.find(
            (tagDropdownItem) =>
              tagDropdownItem.type === TagDropdownItemTypesEnum.SEARCH_TAG &&
              tagDropdownItem.tag[0].id === props.navigationTagsPath?.[0].id,
          )
            ? [{type: TagDropdownItemTypesEnum.SEARCH_TAG as const, tag: props.navigationTagsPath}]
            : [];

        return [...maybeBreadCrumbs, ...maybeParentTagItem, ...dataInParentFolder];
      }

      return data;
    };
  }

  private renderAddTagButton() {
    return (
      <UserThemeConsumer>
        {(theme) => (
          <StyledIconButton
            iconName="plusCircle"
            label={<Trans id="dropdown-tags-header-add-tag">Create new tag</Trans>}
            onClick={(event) => this.props.onCreateTagSelect(event, '')}
            size={VisualSizesEnum.SMALL}
            color={theme.greys.shade80}
            hoverColor={theme.greys.shade80}
          />
        )}
      </UserThemeConsumer>
    );
  }

  private maybeRenderBulkAddConfirmation() {
    const {tagToBulkAdd} = this.state;
    if (!tagToBulkAdd) {
      return null;
    }

    return (
      <BulkActionConfirmationDialog
        onConfirmClick={() => this.props.bulkTagConversation(tagToBulkAdd)}
        onRequestClose={() => this.setState({tagToBulkAdd: undefined})}
        actionType={BulkActionTypesEnum.TAG}
      />
    );
  }

  private maybeRenderBulkRemoveConfirmation() {
    const {tagToBulkRemove} = this.state;
    if (!tagToBulkRemove) {
      return null;
    }

    return (
      <BulkActionConfirmationDialog
        onConfirmClick={() => this.props.bulkUntagConversation(tagToBulkRemove)}
        onRequestClose={() => this.setState({tagToBulkRemove: undefined})}
        actionType={BulkActionTypesEnum.UNTAG}
      />
    );
  }

  private readonly renderRow = (
    item: TagDropdownItem,
    index: number,
    items: ReadonlyArray<TagDropdownItem>,
    requestQuery: string,
  ) => {
    const isSearching = Boolean(requestQuery);

    const hasRequiredTaggingSection = hasRequiredTagging(items);
    const hasRecentlyUsedSection = hasRecentlyUsedTags(items);
    const hasIntegrationsSection = !this.props.isBulkActionRequired;

    const requiredTaggingOffset = 1;
    const recentlyUsedOffset = 1 + countTrueValues(hasRequiredTaggingSection);
    const integrationOffset = 1 + countTrueValues(hasRequiredTaggingSection, hasRecentlyUsedSection);
    const allTagOffset =
      1 + countTrueValues(hasRequiredTaggingSection, hasRecentlyUsedSection, hasIntegrationsSection);

    const isRecentlyUsedTag = item.type === TagDropdownItemTypesEnum.RECENT_TAG;

    const tagLabelOffset = isRecentlyUsedTag ? recentlyUsedOffset : allTagOffset;

    // Real index of the tag.
    // If user is searching or navigating to nested tag folder, ignore the index offsets.
    const adjustedIndex = index - (isSearching || this.props.navigationTagsPath?.length ? 0 : tagLabelOffset);

    if (item.type === TagDropdownItemTypesEnum.REQUIRED_TAGGING) {
      return this.renderRequiredTaggingRow(index - requiredTaggingOffset, item);
    }

    const integrationsHeaderIdx = items.findIndex(computeIsEntryPointHeader);

    if (
      item.type !== TagDropdownItemTypesEnum.RECENT_TAG &&
      item.type !== TagDropdownItemTypesEnum.SEARCH_TAG
    ) {
      return this.renderFixedRow(item, index, integrationsHeaderIdx, integrationOffset);
    }

    return this.renderTagRow(item, adjustedIndex, items, isSearching, isRecentlyUsedTag);
  };

  private renderFixedRow(
    item: TagDropdownItem,
    index: number,
    integrationHeaderIdx: number,
    integrationsOffset: number,
  ) {
    const customLinkOffset = integrationsOffset;

    switch (item.type) {
      case TagDropdownItemTypesEnum.RECENT_TAG_HEADER:
        return (
          <DropdownCategory>
            <Trans id="dropdown-tags-section-recently-used">Recently used</Trans>
          </DropdownCategory>
        );

      case TagDropdownItemTypesEnum.ENTRY_POINT_HEADER:
        return (
          <DropdownCategory>
            <Trans id="dropdown-tags-section-integrations">Integrations</Trans>
          </DropdownCategory>
        );

      case TagDropdownItemTypesEnum.ENTRY_POINT_CUSTOM_LINK:
        return (
          <DropdownItem onSelect={this.props.onAddTopicSelect} index={index - customLinkOffset}>
            <DropdownItemAccentIcon label={null} iconName="dropdownNewLink" />
            <DropdownItemContent>
              <Trans id="dropdown-tags-item-new-link">Link</Trans>
            </DropdownItemContent>
          </DropdownItem>
        );

      case TagDropdownItemTypesEnum.ENTRY_POINT:
        // We need to bump the index the number of headers above the integrations section and the number of
        // recently used tags to make sure we get the right entryPoint.
        return this.renderTopicEntryPointRow(index - integrationsOffset, index - (integrationHeaderIdx + 2));

      case TagDropdownItemTypesEnum.EMPTY_LABEL:
        return (
          <DropdownItem index={index - integrationsOffset} isDisabled>
            <DropdownItemContent>
              <Trans id="dropdown-tags-item-no-tags">No tags</Trans>
            </DropdownItemContent>
          </DropdownItem>
        );

      case TagDropdownItemTypesEnum.REQUIRED_TAGGING_HEADER:
        return (
          <StyledRequiredTaggingCategory>
            <Trans id="dropdown-tags-section-required-tags">Required tags</Trans>
            <StyledRequiredTaggingStarDiv>*</StyledRequiredTaggingStarDiv>
            <StyledRequiredTaggingTooltip conversations={this.props.conversations} />
          </StyledRequiredTaggingCategory>
        );

      case TagDropdownItemTypesEnum.SEARCH_TAG_BREADCRUMBS:
        return (
          <DropdownCategory>
            <FolderBreadcrumbs
              topLevelBreadcrumb={{
                label: <Trans id="add-topic-or-tag-dropdown-tag-breadcrumbs-all">All</Trans>,
                onBreadcrumbClick: () => {
                  trackInterfaceEvent(InterfaceEventsEnum.P3_NAVIGATE_TAG_DROPDOWN, {
                    action: 'navigate_via_breadcrumbs',
                    label: 'all',
                  });
                  this.props.setSearchQuery('');
                  return this.props.setNavigationTagsPath?.([]);
                },
              }}
              folderPathBreadcrumbs={(this.props.navigationTagsPath ?? [])
                .map((tag, ascendingIndex) => ({
                  label: tag.name,
                  onBreadcrumbClick: () => {
                    trackInterfaceEvent(InterfaceEventsEnum.P3_NAVIGATE_TAG_DROPDOWN, {
                      action: 'navigate_via_breadcrumbs',
                      tag_id: tag.id,
                    });
                    this.props.setSearchQuery('');
                    return this.props.setNavigationTagsPath?.(
                      (this.props.navigationTagsPath ?? []).slice(ascendingIndex),
                    );
                  },
                }))
                .reverse()}
            />
          </DropdownCategory>
        );

      default:
        return (
          <DropdownCategory>
            <Trans id="dropdown-tags-section-all-tags">All tags</Trans>
          </DropdownCategory>
        );
    }
  }

  private renderRequiredTaggingRow(rowIndex: number, item: DropdownRequiredTaggingItem) {
    return (
      <AddRequiredTagItem
        requiredTaggingSafetyNet={item.requiredTaggingSafetyNet}
        index={rowIndex}
        conversations={this.props.conversations}
        onTagConversation={(tag) => this.props.tagConversation([tag])}
        onUntagConversation={this.props.untagConversation}
      />
    );
  }

  private renderTopicEntryPointRow(rowIndex: number, itemIndex: number) {
    const {entryPoints, onEntryPointSelect} = this.props;
    const entryPoint = entryPoints[itemIndex];
    if (!entryPoint) {
      throw new Error(`Topic not found for index: ${itemIndex}`);
    }

    const onSelect = onEntryPointSelect && (() => onEntryPointSelect(entryPoint));
    return (
      <DropdownItem index={rowIndex} onSelect={onSelect}>
        {entryPoint.iconUrl && <DropdownItemIconImage source={entryPoint.iconUrl} />}
        <DropdownItemContent>{entryPoint.name}</DropdownItemContent>
      </DropdownItem>
    );
  }

  private renderTagRow(
    item: DropdownRecentTagItem | DropdownSearchTagItem,
    index: number,
    items: ReadonlyArray<TagDropdownItem>,
    isSearching?: boolean,
    isRecent?: boolean,
  ) {
    const {conversations, teams, setNavigationTagsPath, navigationTagsPath} = this.props;
    const tag = item.tag[0];
    const tagAliasDuplicates = getTagAliasDuplicates(items);
    const isSearchingOrRecent = isSearching || isRecent;

    const isChecked = conversations.every((c) => c.tagIds.includes(tag.id));
    const team = teams[tag.namespace];
    const nestingLevel = calculateNestingLevel(navigationTagsPath, isSearchingOrRecent, item.tag);

    const tagsPath = isSearchingOrRecent ? item.tag : [];
    const section = isSearching ? 'search_results' : this.getSectionName(item.type);
    const shouldNavigateToChildTag = Boolean(
      !isRecent &&
        tag.activeChildTagIds?.length &&
        !isSearching &&
        this.props.navigationTagsPath?.[0]?.id !== tag.id,
    );

    const tagNameRenderer = (tagModel: TagModel) => {
      if (!isSearching || isChecked) {
        return tagModel.name;
      }
      const textArray = tagModel.name.split(this.props.searchQuery);

      return (
        <span>
          {textArray.map((segment, i) => (
            <>
              {segment}
              {i !== textArray.length - 1 && <b>{this.props.searchQuery}</b>}
            </>
          ))}
        </span>
      );
    };

    return (
      <TagDropdownItem
        tag={tag}
        tagsPath={tagsPath}
        team={team}
        key={tag.id}
        index={index}
        isChecked={isChecked}
        nestingLevel={nestingLevel}
        shouldCollapseBreadcrumb
        shouldShowDeduplication={tagAliasDuplicates.has(tag.alias)}
        onSelect={(e) => {
          if (shouldNavigateToChildTag) {
            trackInterfaceEvent(InterfaceEventsEnum.P3_NAVIGATE_TAG_DROPDOWN, {
              action: 'navigate_to_subpage',
              tag_id: tag.id,
            });
            e.preventDefault();
            // We reset the search query when navigating to a deeper tag folder, because it might result in
            // an empty result set after navigation.
            this.props.setSearchQuery('');
            return setNavigationTagsPath?.([tag, ...(this.props.navigationTagsPath ?? [])]);
          }

          return this.onToggleTag(e, item.tag, isChecked, section);
        }}
        renderRightContent={() => {
          if (shouldNavigateToChildTag) {
            return <DropdownItemChevron />;
          }

          return <DropdownItemCheckbox isChecked={isChecked} />;
        }}
        tagNameRenderer={tagNameRenderer}
      />
    );
  }

  private readonly renderEmpty = () => {
    const {searchQuery} = this.props;
    if (searchQuery) {
      return (
        <StyledEmptyWrapperDiv>
          <StyledEmptyContentWrapper>
            <StyledDropdownEmpty />
            <StyledEmptyButtonDiv>
              <TagDropdownCreateButton
                onClick={(e) => this.props.onCreateTagSelect(e, searchQuery)}
                variant={ButtonVariantsEnum.LINK}
              >
                <Trans id="dropdown-tags-createquery">Create &quot;{searchQuery}&quot;</Trans>
              </TagDropdownCreateButton>
            </StyledEmptyButtonDiv>
          </StyledEmptyContentWrapper>
        </StyledEmptyWrapperDiv>
      );
    }

    const {onCreateTagSelect, onAddTopicSelect} = this.props;

    return (
      <StyledEmptyWrapperDiv>
        <StyledEmptyContentWrapper>
          <StyledDropdownEmpty imageSource={noTags}>
            <Trans id="dropdown-notags">No tags</Trans>
          </StyledDropdownEmpty>
          <StyledEmptyButtonDiv>
            <TagDropdownCreateButton
              onClick={(e) => onCreateTagSelect(e, '')}
              variant={ButtonVariantsEnum.PRIMARY}
            >
              <Trans id="dropdown-tags-add-tag-btn">Add tag</Trans>
            </TagDropdownCreateButton>
            {onAddTopicSelect ? (
              <TagDropdownCreateButton onClick={onAddTopicSelect} variant={ButtonVariantsEnum.SECONDARY}>
                <Trans id="dropdown-tags-add-link-btn">Add link</Trans>
              </TagDropdownCreateButton>
            ) : null}
          </StyledEmptyButtonDiv>
        </StyledEmptyContentWrapper>
      </StyledEmptyWrapperDiv>
    );
  };
}

/*
 * Helpers.
 */

const getTagAliasDuplicates = memoize()((items: ReadonlyArray<TagDropdownItem>) => {
  const tagAliases = new Set<string>();
  const tagAliasDuplicates = new Set<string>();

  // Loop on all the tags to find the ones with duplicate names.
  items.forEach((item) => {
    if (
      item.type !== TagDropdownItemTypesEnum.RECENT_TAG &&
      item.type !== TagDropdownItemTypesEnum.SEARCH_TAG
    ) {
      return;
    }

    if (!item.tag || item.type === TagDropdownItemTypesEnum.RECENT_TAG) {
      return;
    }

    const tag = item.tag[0];
    if (tagAliases.has(tag.alias)) {
      tagAliasDuplicates.add(tag.alias);
      return;
    }

    tagAliases.add(tag.alias);
  });

  return tagAliasDuplicates;
});

const hasRequiredTagging = memoize()((items: ReadonlyArray<TagDropdownItem>) =>
  items.some((item) => item.type === TagDropdownItemTypesEnum.REQUIRED_TAGGING_HEADER),
);

const hasRecentlyUsedTags = memoize()((items: ReadonlyArray<TagDropdownItem>) =>
  items.some((item) => item.type === TagDropdownItemTypesEnum.RECENT_TAG_HEADER),
);

function computeIsEntryPointHeader(item: TagDropdownItem): boolean {
  return item.type === TagDropdownItemTypesEnum.ENTRY_POINT_HEADER;
}

function calculateNestingLevel(
  navigationTagsPath: ReadonlyArray<TagModel> | undefined,
  isSearchingOrRecent: boolean | undefined,
  tags: SearchTagModel,
): number {
  if (navigationTagsPath === undefined) {
    return 0;
  }

  return isSearchingOrRecent || navigationTagsPath.length === 0 ? 0 : tags.length - navigationTagsPath.length;
}

function calculateDataInParentFolder(
  isSearching: boolean,
  navigationTagsPath: ReadonlyArray<TagModel>,
  data: ReadonlyArray<TagDropdownItem>,
): ReadonlyArray<TagDropdownItem> {
  if (isSearching) {
    return data.filter((tagDropdownItem) => tagDropdownItem.type === TagDropdownItemTypesEnum.SEARCH_TAG);
  }

  return data.filter(
    (tagDropdownItem) =>
      tagDropdownItem.type === TagDropdownItemTypesEnum.SEARCH_TAG &&
      (tagDropdownItem.tag[0].id === navigationTagsPath[0].id ||
        tagDropdownItem.tag[0].parentTagId === navigationTagsPath[0].id),
  );
}
